/** Compact api-requests list embedded in another record's detail view (the
 *  Booking / Order "API Requests" tabs) — the same columns, chips and cell
 *  popovers as the /api-requests grid, over a caller-provided row array.
 *
 *  Clicking a row opens the shared ApiRequestDetailDialog IN PLACE (local
 *  state, not the route — the host page keeps its own URL; the dialog's Share
 *  button still copies the canonical /api-requests/:id/:tab link). Cell
 *  quick-filter actions open the full /api-requests grid IN A NEW TAB with
 *  that chip applied (plus the mandatory 7-day created window) — the host
 *  record stays where it is. Everything that needs
 *  `console_api_requests_view` is hidden when the operator lacks it — the
 *  embedded rows themselves come from the host record's endpoint
 *  (booking/order perms).
 */
import { Typography } from '@mui/material';
import { DataGridPro } from '@mui/x-data-grid-pro';
import { useCallback, useMemo, useState } from 'react';

import type { ApiRequestListItem } from '@/api/apiRequests';
import { useAuth } from '@/auth/AuthProvider';
import { equalityOperator } from '@/components/QuerySearch';
import { apiRequestsHref } from '@/utils/gridLinks';
import { openInNewTab } from '@/utils/openInNewTab';

import { ApiRequestDetailDialog } from './detail/ApiRequestDetailDialog';
import { buildColumns } from './apiRequestsColumns';
import { buildApiRequestNamespaces } from './apiRequestNamespaces';

/** Embedded column subset — the host record already fixes the tenant and the
 *  booking identity, so the wide identity columns add nothing here. */
const FIELDS = new Set([
  'open', 'created_ts', 'status_code', 'integration', 'type', 'booking_summary',
  'error_series_id', 'duration_ms', 'id',
]);

export function EmbeddedApiRequests({ requests }: { requests: ApiRequestListItem[] }) {
  const auth = useAuth();
  const namespaces = useMemo(() => buildApiRequestNamespaces(), []);
  const canView = auth.hasPerm('console_api_requests_view');
  const [openRequest, setOpenRequest] = useState<{ id: number; tab?: string } | null>(null);

  // Quick filters open the full grid (new tab) with the chip pre-applied;
  // apiRequestsHref adds the created window the grid insists on.
  const onQuickFilter = useCallback(
    (namespace: string, value: string) => {
      if (namespace === 'tenant') {
        openInNewTab(apiRequestsHref([], { tenant: value }));
        return;
      }
      const cfg = namespaces[namespace];
      const operator = cfg ? equalityOperator(cfg.operators) : null;
      if (!operator) return;
      openInNewTab(apiRequestsHref([{ kind: 'cond', namespace, operator, value }]));
    },
    [namespaces],
  );

  const detailPath = useCallback((id: number) => `/api-requests/${id}/info`, []);

  const columns = useMemo(() => {
    const cols = buildColumns({ showTenant: false, onQuickFilter, detailPath }).filter((c) =>
      FIELDS.has(c.field),
    );
    // Without the api-requests permission the detail routes/dialog are
    // off-limits — drop the open column (the rows themselves come from the
    // host record's endpoint).
    return canView ? cols : cols.filter((c) => c.field !== 'open');
  }, [onQuickFilter, detailPath, canView]);

  if (!requests.length) {
    return (
      <Typography variant="body2" color="text.disabled" sx={{ p: 2 }}>
        No API requests.
      </Typography>
    );
  }

  return (
    <>
      <DataGridPro
        rows={requests}
        columns={columns}
        getRowId={(r) => r.id}
        density="compact"
        autoHeight
        hideFooter
        disableColumnFilter
        disableColumnMenu
        disableRowSelectionOnClick
        getRowHeight={() => 'auto'}
        initialState={{ sorting: { sortModel: [{ field: 'created_ts', sort: 'desc' }] } }}
        sx={{
          '& .MuiDataGrid-cell': { display: 'flex', alignItems: 'center', minHeight: 40 },
          ...(canView ? { '& .MuiDataGrid-row': { cursor: 'pointer' } } : {}),
        }}
        {...(canView
          ? { onRowClick: (p: { id: string | number }) => setOpenRequest({ id: Number(p.id) }) }
          : {})}
      />
      {openRequest && canView && (
        <ApiRequestDetailDialog
          requestId={String(openRequest.id)}
          tab={openRequest.tab}
          onClose={() => setOpenRequest(null)}
          onNavigateTab={(t) => setOpenRequest({ id: openRequest.id, tab: t })}
        />
      )}
    </>
  );
}
